/*!
This file is part of CycloneDX JavaScript Library.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

SPDX-License-Identifier: Apache-2.0
Copyright (c) OWASP Foundation. All Rights Reserved.
*/

import type { Comparable } from '../../_helpers/sortable'
import { SortableComparables } from '../../_helpers/sortable'
import type { RatingMethod, Severity } from '../../enums/vulnerability'
import type { Source } from './source'

export interface OptionalRatingProperties {
  source?: Rating['source']
  score?: Rating['score']
  severity?: Rating['severity']
  method?: Rating['method']
  vector?: Rating['vector']
  justification?: Rating['justification']
}

export class Rating implements Comparable<Rating> {
  source?: Source
  score?: number
  severity?: Severity
  method?: RatingMethod
  vector?: string
  justification?: string

  constructor (op: OptionalRatingProperties = {}) {
    this.source = op.source
    this.score = op.score
    this.severity = op.severity
    this.method = op.method
    this.vector = op.vector
    this.justification = op.justification
  }

  /* eslint complexity: ["error", 50] -- acknowledged  */
  compare (other: Rating): number {
    /* eslint-disable @typescript-eslint/strict-boolean-expressions -- run compares in weighted order */
    return ((this.score ?? 0) - (other.score ?? 0)) ||
      (this.vector ?? '').localeCompare(other.vector ?? '') ||
      (this.justification ?? '').localeCompare(other.justification ?? '') ||
      (this.severity ?? '').localeCompare(other.severity ?? '') ||
      (this.method ?? '').localeCompare(other.method ?? '') ||
      (this.source && other.source ? this.source.compare(other.source) : 0)
    /* eslint-enable  @typescript-eslint/strict-boolean-expressions */
  }
}

export class RatingRepository extends SortableComparables<Rating> {}
